// src/components/Clients.js
// Clients finaux — /clients. One card: header (count, debounced server-side
// search, per-page VIP filter chip — the API has no VIP param, so the label
// says so —, quiet refresh, primary sync-from-tickets with a translated
// inline result banner), geometry table (VIP crown inline, translated source
// chips, dates without seconds), row click → /clients/:id. URL state
// (?q&page&size&vip). API: GET /api/clients (skip/limit/search — no total,
// hence prev/next paging), POST /api/clients/sync.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaSearch, FaSyncAlt, FaCloudDownloadAlt, FaCrown, FaTimes,
  FaCheckCircle, FaTimesCircle, FaUsers, FaChevronRight
} from 'react-icons/fa';
import './Clients.css';

const PAGE_SIZES = [25, 50, 100];
const SYNC_LIMIT = 1500;

const SOURCE_KEYS = {
  WEB_FORM: 'clientsPage.sourceWebForm',
  OUTLOOK: 'clientsPage.sourceOutlook',
  TICKET_SYNC: 'clientsPage.sourceTicketSync',
  GMAIL: 'clientsPage.sourceGmail',
  MANUAL: 'clientsPage.sourceManual',
};

const Clients = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') || '';
  const sizeParam = Number(searchParams.get('size'));
  const pageSize = PAGE_SIZES.includes(sizeParam) ? sizeParam : 50;
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const vipOnly = searchParams.get('vip') === '1';

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  };

  // Debounced search input → URL
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput.trim() !== searchQuery) {
        updateParams({ q: searchInput.trim(), page: null });
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [clients, setClients] = useState(null); // null = loading
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  const showFeedback = (type, message) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, message });
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedback(null), 8000);
    }
  };
  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const fetchClients = useMemo(() => {
    return async ({ pageArg, limit, searchArg }) => {
      setClients(null);
      setError(null);
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/');
        return;
      }
      try {
        const response = await axios.get('/api/clients', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            skip: (pageArg - 1) * limit,
            limit,
            search: searchArg || undefined,
          },
        });
        const payload = Array.isArray(response.data) ? response.data : [];
        setClients(payload);
        setHasMore(payload.length === limit);
      } catch (err) {
        setError(err.response?.data?.detail || t('clientsPage.fetchError', 'Impossible de charger les clients.'));
        setClients([]);
        setHasMore(false);
      }
    };
  }, [navigate, t]);

  useEffect(() => {
    fetchClients({ pageArg: page, limit: pageSize, searchArg: searchQuery });
  }, [page, pageSize, searchQuery, fetchClients]);

  const handleSync = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { navigate('/'); return; }
    setIsSyncing(true);
    setFeedback(null);
    try {
      const response = await axios.post('/api/clients/sync', null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: SYNC_LIMIT },
      });
      const data = response.data || {};
      showFeedback('success', t('clientsPage.syncResult', '{{processed}} tickets traités · {{created}} clients créés · {{skipped}} ignorés', {
        processed: data.processed ?? 0,
        created: data.created ?? 0,
        skipped: data.skipped ?? 0,
      }));
      if (page !== 1) updateParams({ page: null });
      else fetchClients({ pageArg: 1, limit: pageSize, searchArg: searchQuery });
    } catch (err) {
      showFeedback('error', err.response?.data?.detail || t('clientsPage.syncError', 'Échec de la synchronisation des clients.'));
    } finally {
      setIsSyncing(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(i18n.language || 'fr', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const sourceLabel = (source) => {
    if (!source) return null;
    const key = SOURCE_KEYS[source];
    return key ? t(key, source.toLowerCase().replace(/_/g, ' ')) : source.toLowerCase().replace(/_/g, ' ');
  };

  const loading = clients === null;
  const list = clients || [];
  // The API has no VIP filter — this filter only applies to the loaded page,
  // and the UI says so.
  const displayed = vipOnly ? list.filter((client) => client.is_vip) : list;

  return (
    <div className="clients-page">
      <div className="cls-card">
        {/* Header */}
        <div className="cls-card-header">
          <div className="cls-header-left">
            <h1>{t('clientsPage.titleB2C', 'Clients B2C')}</h1>
            {!loading && (
              <span className="cls-count">
                {vipOnly
                  ? t('clientsPage.countVip', '{{shown}} VIP / {{total}} affichés', { shown: displayed.length, total: list.length })
                  : t('clientsPage.countShown', '{{total}} affichés', { total: list.length })}
              </span>
            )}
          </div>
          <div className="cls-header-right">
            <div className="cls-search">
              <FaSearch aria-hidden="true" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('clientsPage.searchPlaceholder', 'Rechercher par email ou nom…')}
              />
              {searchInput && (
                <button
                  type="button"
                  className="cls-search-clear"
                  onClick={() => { setSearchInput(''); updateParams({ q: null, page: null }); }}
                  aria-label={t('common.cancel', 'Effacer')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            <button
              type="button"
              className={`cls-vip-chip ${vipOnly ? 'on' : ''}`}
              aria-pressed={vipOnly}
              onClick={() => updateParams({ vip: vipOnly ? null : '1' })}
              title={t('clientsPage.vipFilterHint', 'Filtre appliqué à la page affichée')}
            >
              <FaCrown /> {t('clientsPage.vipOnly', 'VIP uniquement')}
            </button>
            <button
              type="button"
              className="cls-icon-btn"
              onClick={() => fetchClients({ pageArg: page, limit: pageSize, searchArg: searchQuery })}
              disabled={loading || isSyncing}
              title={t('clientsPage.refresh', 'Actualiser')}
              aria-label={t('clientsPage.refresh', 'Actualiser')}
            >
              <FaSyncAlt />
            </button>
            <button type="button" className="u-btn u-btn--primary" onClick={handleSync} disabled={isSyncing}>
              <FaCloudDownloadAlt />
              {isSyncing ? t('clientsPage.syncing', 'Synchronisation…') : t('clientsPage.sync', 'Synchroniser depuis les tickets')}
            </button>
          </div>
        </div>

        {feedback && (
          <div className={`cls-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            {feedback.message}
          </div>
        )}

        {error && !feedback && (
          <div className="cls-feedback error" role="alert">
            <FaTimesCircle /> {error}
            <button
              type="button"
              className="u-btn u-btn--secondary"
              onClick={() => fetchClients({ pageArg: page, limit: pageSize, searchArg: searchQuery })}
            >
              {t('clientsPage.retry', 'Réessayer')}
            </button>
          </div>
        )}

        {loading ? (
          <div className="cls-skeleton" aria-hidden="true">
            {Array.from({ length: 8 }, (_, i) => <div key={i} className="cls-skeleton-row" />)}
          </div>
        ) : displayed.length === 0 ? (
          <div className="cls-empty">
            <FaUsers className="cls-empty-icon" />
            <p>
              {vipOnly
                ? t('clientsPage.emptyVip', 'Aucun client VIP sur cette page.')
                : searchQuery
                  ? t('clientsPage.emptySearch', 'Aucun client ne correspond à la recherche.')
                  : t('clientsPage.empty', 'Aucun client pour le moment — lancez une synchronisation depuis les tickets.')}
            </p>
          </div>
        ) : (
          <div className="cls-table-scroll">
            <table className="cls-table">
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '36px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('clientsPage.email', 'Email')}</th>
                  <th>{t('clientsPage.name', 'Nom')}</th>
                  <th>{t('clientsPage.source', 'Source')}</th>
                  <th>{t('clientsPage.createdAt', 'Créé')}</th>
                  <th>{t('clientsPage.updatedAt', 'Modifié')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {displayed.map((client) => (
                  <tr
                    key={client.id}
                    className="cls-row"
                    tabIndex={0}
                    onClick={() => navigate(`/clients/${client.id}`, { state: { client } })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/clients/${client.id}`, { state: { client } });
                    }}
                  >
                    <td className="cls-cell-email" title={client.email}>{client.email}</td>
                    <td title={client.name || ''}>
                      <span className="cls-name">
                        {client.is_vip && (
                          <span className="cls-vip-crown" title="VIP" aria-label="VIP"><FaCrown /></span>
                        )}
                        {client.name || <span className="cls-muted-dash">—</span>}
                      </span>
                    </td>
                    <td>
                      {client.source
                        ? <span className="cls-source-chip">{sourceLabel(client.source)}</span>
                        : <span className="cls-muted-dash">—</span>}
                    </td>
                    <td>{formatDate(client.created_at)}</td>
                    <td>{formatDate(client.updated_at)}</td>
                    <td className="cls-cell-chevron"><FaChevronRight aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (page > 1 || hasMore) && (
          <div className="cls-card-footer">
            <label className="cls-size-select">
              {t('clientsPage.pageSize', 'Par page')}
              <select
                value={pageSize}
                onChange={(e) => updateParams({ size: Number(e.target.value) === 50 ? null : e.target.value, page: null })}
              >
                {PAGE_SIZES.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <nav className="cls-pager" aria-label={t('clientsPage.pagination', 'Pagination')}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => updateParams({ page: page - 1 <= 1 ? null : page - 1 })}
              >
                ‹ {t('clientsPage.previous', 'Précédent')}
              </button>
              <span className="cls-page-indicator">{t('clientsPage.pageN', 'Page {{page}}', { page })}</span>
              <button type="button" disabled={!hasMore} onClick={() => updateParams({ page: page + 1 })}>
                {t('clientsPage.next', 'Suivant')} ›
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
